import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import FeedService, { FeedResponse } from '@apis/FeedService';
import { useEffect } from 'react';
import { registerFeedUpdateListener } from './useNotifications';
import { objectiveFeedQueryKeys } from './useObjectiveFeed';

export const feedQueryKeys = {
  all: ['feed'] as const,
  list: () => ['feed', 'list'] as const,
};

/**
 * Hook for fetching the home feed with cursor-based infinite pagination.
 */
export function useFeed(options?: { enabled?: boolean }) {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInfiniteQuery<FeedResponse>({
    queryKey: feedQueryKeys.list(),
    queryFn: async ({ pageParam }) => {
      return FeedService.getFeed(20, pageParam as string | undefined);
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    enabled: options?.enabled !== false,
  });

  const posts = data?.pages.flatMap((page) => page.items) ?? [];

  return {
    posts,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    error,
    refetch,
  };
}

/**
 * Hook that subscribes to real-time feed updates via the shared
 * notification WebSocket. When a new feed post arrives, the feed
 * query cache is invalidated so the UI refreshes automatically.
 *
 * Uses the module-level registerFeedUpdateListener from useNotifications
 * so it doesn't create a second WebSocket connection.
 */
export function useFeedWebSocket() {
  const queryClient = useQueryClient();

  useEffect(() => {
    registerFeedUpdateListener((feedUpdate) => {
      console.log('📰 WebSocket: New feed post:', feedUpdate.post_id);
      queryClient.invalidateQueries({ queryKey: feedQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: objectiveFeedQueryKeys.all });
    });

    return () => {
      registerFeedUpdateListener(null);
    };
  }, [queryClient]);
}
